import React, { useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import TextInputComponent from '../../../shared/components/TextInputComponent'
import { useDispatch, useSelector } from 'react-redux'
import { getActiveMember, getPrefersDarkMode } from '../../../store/settings/settings.selectors'
import { Allergy, DietType, Entity, FetchResponse, Member, Method, Title } from '../../../shared/types'

import DropDownComponent from '../../../shared/components/DropDownComponent'
import ButtonComponent from '../../../shared/components/ButtonComponent'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckBoxComponent from '../../../shared/components/CheckBoxComponent'
import SwitchComponent from '../../../shared/components/SwitchComponent'

import * as settingsActions from '../../../store/settings/settings.actions'
import { setMessage } from '../../../store/settings/settings.actions'
import DatePickerComponent from '../../../shared/components/DatePickerComponent'
import getCustomDateFromDate from '../../../util/getCustomDateFromDate'
import getDateFromCustomDate from '../../../util/getDateFromCustomDate'
import fetchData from '../../../fetch/fetchData'
import { getApiVersion } from '../../../shared/config/getApiVersion'
import Env from '../../../shared/config/envConfig'
import * as dataActions from '../../../store/data/data.actions'
import ResetPassword from './ResetPassword'
import { addCsrfHeaders } from '../../../shared/config/csrf'

const UserProfile: React.FC = () => {
  const dispatch = useDispatch()
  const theme: Theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  const darkMode: boolean = useSelector(getPrefersDarkMode)

  const basePath = Env.restBase
  const fetchHeaders = Env.fetchHeaders
  addCsrfHeaders(fetchHeaders)

  const storedActiveMember = useSelector(getActiveMember)

  const [activeMember, setActiveMember] = useState<Member | undefined>(storedActiveMember)
  const [expanded, setExpanded] = React.useState<string | false>('stammdaten')
  const [showChangePasswordDialog, setShowChangePasswordDialog] = React.useState<boolean>(false)

  const setMembers = (data: FetchResponse) => {
    dispatch(dataActions.setMembers({ count: data.count, results: data.results }))
  }

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false)
    }

  return (
    <Paper sx={{ margin: downSm ? '0px' : '32px', padding: downSm ? '8px' : '32px' }}>
      <div>
        <Accordion
          sx={{ margin: '0px!important', borderBottom: `1px solid ${theme.palette.grey.A400}` }}
          TransitionProps={{ unmountOnExit: true }} expanded={expanded === 'stammdaten'}
          onChange={handleChange('stammdaten')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Stammdaten</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: downSm ? 'wrap' : 'nowrap',
            }}
            >
              <Box sx={{ marginRight: downSm ? 0 : '20px' }}>
                <DropDownComponent
                  value={activeMember?.title}
                  options={
                    [{
                      key: Title.dr,
                      value: 'Dr.',
                    },
                    {
                      key: Title.prof,
                      value: 'Prof.',
                    },
                    {
                      key: Title.profDr,
                      value: 'Prof. Dr.',
                    }]
                  }
                  label='Titel'
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData: Member = {
                        ...activeMember,
                        title: value ? value.key as Title : undefined,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
                <TextInputComponent
                  label='Vorname'
                  fullWidth
                  value={activeMember?.firstname}
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        firstname: value as string,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
                <TextInputComponent
                  label='Weitere Vornamen'
                  fullWidth
                  value={activeMember?.middlenames}
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        middlenames: value as string,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
                <TextInputComponent
                  label='Nachname'
                  fullWidth
                  value={activeMember?.lastname}
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        lastname: value as string,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
                <TextInputComponent
                  label='Geburtsname'
                  fullWidth
                  value={activeMember?.birthname}
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        birthname: value as string,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
                <DatePickerComponent
                  label='Geboren am'
                  fullWidth
                  value={activeMember?.birthday ? getDateFromCustomDate(activeMember?.birthday) : undefined}
                  onChangeDate={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        birthday: value ? getCustomDateFromDate(value) : {},
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
                <TextInputComponent
                  label='Geboren in'
                  clearable={false}
                  fullWidth
                  value={activeMember?.placeOfBirth}
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        placeOfBirth: value as string,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
              </Box>
              <Box sx={{ width: '500px', overflow: 'hidden', marginTop: '8px' }}>
                <img
                  style={{ width: '100%', height: 'auto', borderRadius: '4px' }}
                  alt={`${activeMember?.firstname} ${activeMember?.middlenames ? activeMember?.middlenames : ''} ${activeMember?.lastname}`}
                  src={activeMember?.profileImageUrl ? activeMember?.profileImageUrl : `./profileImages/${activeMember?.id}.jpg`}
                />

                <CheckBoxComponent
                  label='Ich stimme zu, dass meine Geburtsdaten (Geburtstag, -ort) und Wohnort für andere Familienmitglieder auf dieser Webseite sichtbar sind.'
                  value={activeMember?.showDetails}
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        showDetails: value,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion
          sx={{ margin: '0px!important', borderBottom: `1px solid ${theme.palette.grey.A400}` }}
          TransitionProps={{ unmountOnExit: true }}
          expanded={expanded === 'kontakt'}
          onChange={handleChange('kontakt')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Kontaktdaten</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: downSm ? 'wrap' : 'nowrap',
            }}
            >
              <Box sx={{ marginRight: downSm ? 0 : '20px', width: downSm ? '100%' : '50%' }}>
                <TextInputComponent
                  label='Straße & Hausnummer'
                  fullWidth
                  value={activeMember?.address}
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        address: value as string,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
                <TextInputComponent
                  label='Stadt'
                  fullWidth
                  value={activeMember?.city}
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        city: value as string,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
                <TextInputComponent
                  label='Telefon'
                  fullWidth
                  value={activeMember?.phone}
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        phone: value as string,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
              </Box>
              <Box sx={{ width: downSm ? '100%' : '50%' }}>
                <TextInputComponent
                  label='PLZ'
                  fullWidth
                  value={activeMember?.zip}
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        zip: value as string,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
                <TextInputComponent
                  label='Land'
                  fullWidth
                  value={activeMember?.country || 'Deutschland'}
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        country: value as string,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
                <TextInputComponent
                  label='E-Mail'
                  fullWidth
                  value={activeMember?.email}
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        email: value as string,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion
          sx={{ margin: '0px!important', borderBottom: `1px solid ${theme.palette.grey.A400}` }}
          TransitionProps={{ unmountOnExit: true }}
          expanded={expanded === 'allergien'}
          onChange={handleChange('allergien')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Allergien / Unverträglichkeiten</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: downSm ? 'wrap' : 'nowrap',
            }}
            >
              <Box sx={{ marginRight: downSm ? 0 : '20px', width: downSm ? '100%' : '50%' }}>
                <DropDownComponent
                  value={activeMember?.dietType}
                  options={
                    [{
                      key: DietType.vegetarian,
                      value: 'Vegetarisch',
                    },
                    {
                      key: DietType.vegan,
                      value: 'Vegan',
                    },
                    {
                      key: DietType.custom,
                      value: 'Sonstiges',
                    }]
                  }
                  label='Ernährungsform'
                  onChange={(value) => {
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        dietType: value ? value.key as DietType : undefined,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
              </Box>
              <Box sx={{ width: downSm ? '100%' : '50%' }}>
                <DropDownComponent
                  multipleValue={activeMember?.allergies}
                  multiple
                  options={
                    [{
                      key: Allergy.nuts,
                      value: 'Nüsse',
                    },
                    {
                      key: Allergy.lactose,
                      value: 'Laktose',
                    },
                    {
                      key: Allergy.sesame,
                      value: 'Sesam',
                    },
                    {
                      key: Allergy.fish,
                      value: 'Fisch',
                    },
                    {
                      key: Allergy.eggs,
                      value: 'Eier',
                    },
                    {
                      key: Allergy.gluten,
                      value: 'Gluten',
                    },
                    {
                      key: Allergy.mustard,
                      value: 'Senf',
                    },
                    {
                      key: Allergy.celery,
                      value: 'Sellerie',
                    },
                    {
                      key: Allergy.soy,
                      value: 'Soja',
                    }]
                  }
                  label='Allergien'
                  onChangeList={(value) => {
                    const list: Allergy[] = []
                    value.map(elem => {
                      list.push(elem.key as Allergy)
                    })
                    if (activeMember) {
                      const newMemberData = {
                        ...activeMember,
                        allergies: list,
                      }
                      setActiveMember(newMemberData)
                    }
                  }}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion
          sx={{ margin: '0px!important', borderBottom: `1px solid ${theme.palette.grey.A400}` }}
          TransitionProps={{ unmountOnExit: true }}
          expanded={expanded === 'settings'}
          onChange={handleChange('settings')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Einstellungen</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <Box sx={{ width: '100%', margin: '0 0 20px 0' }}>
                <ButtonComponent
                  text='Passwort ändern'
                  onClick={() => setShowChangePasswordDialog(true)}
                />
              </Box>
              <Box sx={{ width: '100%' }}>
                <SwitchComponent
                  value={darkMode}
                  onChange={(checked) => {
                    localStorage.setItem('darkMode', checked.toString())
                    dispatch(settingsActions.setPrefersDarkMode(checked))
                  }}
                  label='Dark Mode'
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </div>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginTop: '20px' }}>
        <ButtonComponent
          color='primary'
          variant='text'
          text='Speichern'
          onClick={() => {
            fetchData(basePath, getApiVersion(), fetchHeaders, Entity.members, Method.PUT, activeMember?.originId, { member: activeMember }, setMembers, setMessage)
          }}
        />
      </Box>
      <ResetPassword showDialog={showChangePasswordDialog} setShowDialog={setShowChangePasswordDialog} />
    </Paper>
  )
}

export default UserProfile
